import { EXAMPLE_IDS } from './example-ids.js';
import { EXAMPLE_NOW } from './examples.js';
/**
 * The success skip an example naming a run of `demo@p2a` declared until CS r4b wrote the layer
 * (CS r4a deliverable 112). No example declares it since r4b (deliverable 124); kept as an export.
 * The six file-bound skips of DECISIONS 492 give their own reasons.
 */
export const SEEDED_BY_P2A = { skip: 'seeded by demo@p2a (CS r4b)' };
/** The synthetic export's Notion ids: two databases and two pages. */
export const EXAMPLE_NOTION_IDS = {
    npcs: '5a1c3e7f9b2d4f6a8c0e1f3a5b7d9c01',
    locations: '5a1c3e7f9b2d4f6a8c0e1f3a5b7d9c02',
    rell: '7c2e4a6b8d0f4e1a9b3c5d7e9f1a2b01',
    oldQuay: '7c2e4a6b8d0f4e1a9b3c5d7e9f1a2b02',
};
const n = EXAMPLE_NOTION_IDS;
const PLAN_HASH = '3f9a1c7e5b2d4f608a1c3e5f7b9d2f4a6c8e0a2c4e6a8c0e2a4c6e8a0c2e4a6c';
const MiB = 1024 * 1024;
/** Twenty MiB: three parts of `IMPORT_PART_BYTES`, the last of 4 MiB. */
export const EXAMPLE_IMPORT_BYTES = 20 * MiB;
/** One upload part's bytes, as a multipart example's file (a few bytes of a zip's start). */
export const EXAMPLE_IMPORT_PART = {
    name: 'part-1',
    type: 'application/octet-stream',
    base64: 'UEsDBBQAAAAIAA==',
};
/** The plan's head (`import.runs.plan`): the two databases and their two-way relation. */
export const EXAMPLE_IMPORT_PLAN_OUTLINE = {
    version: 1,
    source: 'notion',
    exportKind: 'html',
    planHash: PLAN_HASH,
    stats: { databases: 2, pages: 24, relations: 9, images: 3, warnings: { image_missing: 1 } },
    databases: [
        {
            notionId: n.npcs,
            title: 'NPCs',
            entry: `NPCs ${n.npcs}.html`,
            proposedType: { key: 'npc', name: 'NPC', existing: true },
            properties: [
                { notionName: 'Faction', proposedKind: 'select', options: ['Lamplighters', 'Tide Watch'] },
                {
                    notionName: 'Lives in',
                    proposedKind: 'relation',
                    options: [],
                    relationDatabaseId: n.locations,
                    twoWayPartner: 'Residents',
                    proposedLinkType: { key: 'lives_in', name: 'Lives in', inverseName: 'Residents' },
                },
            ],
        },
        {
            notionId: n.locations,
            title: 'Locations',
            entry: `Locations ${n.locations}.html`,
            proposedType: { key: 'location', name: 'Location', existing: true },
            properties: [
                {
                    notionName: 'Residents',
                    proposedKind: 'relation',
                    options: [],
                    relationDatabaseId: n.npcs,
                    twoWayPartner: 'Lives in',
                },
            ],
        },
    ],
};
/** The mapping the plan job proposes, with the NPCs' faction kept as text. */
export const EXAMPLE_IMPORT_MAPPING = {
    databases: [
        { databaseId: n.npcs, typeKey: 'npc' },
        { databaseId: n.locations, typeKey: 'location' },
    ],
    properties: [
        { databaseId: n.npcs, notionName: 'Faction', fieldKey: null },
        { databaseId: n.npcs, notionName: 'Lives in', fieldKey: 'lives_in' },
    ],
    statuses: [],
    defaults: { pair: 'pair', changedInBoth: 'keep_mine' },
};
const ids = EXAMPLE_IDS;
function run(fields) {
    return {
        fileName: 'drowned-lanterns-workspace.zip',
        bytes: EXAMPLE_IMPORT_BYTES,
        partsTotal: 3,
        partsReceived: 3,
        exportKind: null,
        planHash: null,
        stats: null,
        mapping: null,
        counts: null,
        undoRefused: 0,
        failure: null,
        cancelRequestedAt: null,
        createdAt: EXAMPLE_NOW,
        updatedAt: EXAMPLE_NOW,
        appliedAt: null,
        undoneAt: null,
        ...fields,
    };
}
const planned = {
    exportKind: 'html',
    planHash: PLAN_HASH,
    stats: EXAMPLE_IMPORT_PLAN_OUTLINE.stats,
};
/** `import.runs.create`'s answer: a new run waiting for its three parts. */
export const EXAMPLE_IMPORT_RUN_CREATED = run({
    id: ids.importRunCreated.id,
    rv: 1,
    status: 'uploading',
    partsReceived: 0,
});
/** `import.runs.complete`'s answer: every part in, the plan job queued. */
export const EXAMPLE_IMPORT_RUN_PLANNING = run({
    id: ids.importRunUploading.id,
    rv: 3,
    status: 'planning',
});
/** `import.runs.cancel`'s answer for the run still uploading. */
export const EXAMPLE_IMPORT_RUN_CANCELLED = run({
    id: ids.importRunUploading.id,
    rv: 3,
    status: 'cancelled',
    partsReceived: 1,
});
/** The planned run, its mapping proposed. */
export const EXAMPLE_IMPORT_RUN_PLANNED = run({
    id: ids.importRunPlanned.id,
    rv: 2,
    status: 'planned',
    ...planned,
    mapping: EXAMPLE_IMPORT_MAPPING,
});
/** The mapping `import.runs.putMapping`'s example saves: the faction to a field. */
export const EXAMPLE_IMPORT_MAPPING_SAVED = {
    ...EXAMPLE_IMPORT_MAPPING,
    properties: [
        { databaseId: n.npcs, notionName: 'Faction', fieldKey: 'faction' },
        { databaseId: n.npcs, notionName: 'Lives in', fieldKey: 'lives_in' },
    ],
};
/** `import.runs.putMapping`'s answer: the GM's mapping saved. */
export const EXAMPLE_IMPORT_RUN_MAPPED = run({
    id: ids.importRunPlanned.id,
    rv: 3,
    status: 'planned',
    ...planned,
    mapping: EXAMPLE_IMPORT_MAPPING_SAVED,
});
/** `import.runs.apply`'s answer: the apply job queued. */
export const EXAMPLE_IMPORT_RUN_APPLYING = run({
    id: ids.importRunPlanned.id,
    rv: 3,
    status: 'applying',
    ...planned,
    mapping: EXAMPLE_IMPORT_MAPPING,
});
/** The newest applied run, with its counts. */
export const EXAMPLE_IMPORT_RUN_APPLIED = run({
    id: ids.importRunApplied.id,
    rv: 5,
    status: 'applied',
    ...planned,
    mapping: EXAMPLE_IMPORT_MAPPING,
    counts: { created: 19, filled: 1, paired: 1, kept_both: 1, skipped: 2 },
    createdAt: '2026-09-12T18:00:00.000Z',
    appliedAt: '2026-09-12T18:05:00.000Z',
});
/** `import.runs.undo`'s answer: the undo job queued. */
export const EXAMPLE_IMPORT_RUN_UNDOING = {
    ...EXAMPLE_IMPORT_RUN_APPLIED,
    rv: 6,
    status: 'undoing',
};
/** The page titled as the demo's stub: Fill, by default, into the stub. */
export const EXAMPLE_IMPORT_PAGE = {
    notionId: n.rell,
    title: 'Guard-Captain Rell',
    databaseId: n.npcs,
    depth: 1,
    typeKey: 'npc',
    tier: 'stub',
    candidates: [{ documentId: ids.npcGuardCaptainRell.id, typeKey: 'npc', isAlias: false }],
    inExportDuplicate: false,
    documentId: ids.npcGuardCaptainRell.id,
    reimport: null,
    similarity: null,
    srdDocumentId: null,
    choice: 'fill',
    choiceBy: 'default',
    skippedBefore: false,
    outcome: null,
    warnings: [],
};
/** The same page with its body, as Compare shows it beside the stub. */
export const EXAMPLE_IMPORT_PAGE_DETAIL = {
    ...EXAMPLE_IMPORT_PAGE,
    body: {
        type: 'doc',
        content: [
            {
                type: 'paragraph',
                attrs: { id: '0b5e2f1c-8a3d-5c47-9e61-2f4a6c8e0b11' },
                content: [
                    {
                        type: 'text',
                        text: 'Rell keeps the Tide Watch at the Old Quay and owes the abbot a favor.',
                    },
                ],
            },
            {
                type: 'paragraph',
                attrs: { id: '0b5e2f1c-8a3d-5c47-9e61-2f4a6c8e0b12' },
                content: [{ type: 'text', text: 'She will not speak of the night the bells fell silent.' }],
            },
        ],
    },
};
/** The dry run of the planned run. */
export const EXAMPLE_IMPORT_DRY_RUN = {
    byTypeAndChoice: [
        { typeKey: 'npc', choice: 'create', pages: 10 },
        { typeKey: 'npc', choice: 'fill', pages: 1 },
        { typeKey: 'location', choice: 'create', pages: 8 },
        { typeKey: null, choice: 'create', pages: 3 },
    ],
    unchanged: 0,
    changedInBoth: 0,
    gone: 0,
    unresolvedRelations: 1,
    dateFailures: [],
    longPages: [],
    images: { found: 2, missing: 1 },
    srdNotes: 1,
    warnings: { image_missing: 1, relation_target_missing: 1 },
    undecided: { pages: 2, databases: 0 },
};
// ---- CS r4c deliverable 131: single pages and value failures ----
/**
 * A `markdown_pages` export's plan head with its single pages (CS r4c): two scene pages whose
 * property blocks hold a tide (select), whether the lamp was lit, when it was last seen, where it
 * takes place and who stands with them (two relations whose targets lie outside the export).
 */
export const EXAMPLE_IMPORT_PLAN_OUTLINE_PAGES = {
    version: 1,
    source: 'notion',
    exportKind: 'markdown_pages',
    planHash: '6e2a4c8e0b1d3f5a6e2a4c8e0b1d3f5a6e2a4c8e0b1d3f5a6e2a4c8e0b1d3f5a',
    stats: {
        databases: 0,
        pages: 2,
        relations: 4,
        images: 0,
        warnings: { relation_target_missing: 4 },
    },
    databases: [],
    singlePages: {
        proposedType: { key: 'note', name: 'Note', existing: true },
        properties: [
            { notionName: 'Tide', proposedKind: 'select', options: ['Rising', 'Ebbing'] },
            { notionName: 'Lamp lit', proposedKind: 'boolean', options: [] },
            { notionName: 'Last seen', proposedKind: 'real_date', options: [] },
            { notionName: 'Where', proposedKind: 'relation', options: [] },
            {
                notionName: 'Allies',
                proposedKind: 'relation',
                options: [],
                proposedLinkType: { key: 'allied_with', name: 'Allied with' },
            },
        ],
    },
};
/** The GM's mapping of those pages: scenes as notes, the block accepted, relation stubs typed. */
export const EXAMPLE_IMPORT_MAPPING_PAGES = {
    databases: [],
    properties: [],
    statuses: [],
    propertyBlock: 'accept',
    singlePages: {
        typeKey: 'note',
        properties: [
            { notionName: 'Tide', fieldKey: 'tide' },
            { notionName: 'Lamp lit', fieldKey: null },
            { notionName: 'Last seen', fieldKey: 'last_seen', dateAs: 'real_date' },
            { notionName: 'Where', fieldKey: 'where', stubTypeKey: 'location' },
            { notionName: 'Allies', fieldKey: 'allies', stubTypeKey: 'npc' },
        ],
    },
    defaults: { pair: 'pair', changedInBoth: 'keep_mine' },
};
/** What an apply could not write: a single page's date, and two of the NPCs' factions. */
export const EXAMPLE_IMPORT_VALUE_FAILURES = [
    { databaseId: null, notionName: 'Last seen', fieldKey: 'last_seen', failures: 1 },
    { databaseId: n.npcs, notionName: 'Faction', fieldKey: 'faction', failures: 2 },
];
//# sourceMappingURL=examples-import.js.map